import { BlobServiceClient } from "@azure/storage-blob";
import uuid from "react-uuid";
import {
  DETAIL_QUALITY,
  OVERVIEW_QUALITY,
  compressImage,
} from "../../Common/Photo/compressImage";
import { readCaptureTime } from "../../Common/Photo/captureTime";
import {
  VehiclePhotoAngle,
  VehiclePhotoInfo,
  VehiclePhotoPhase,
  VehiclePhotoUpload,
} from "../../../system/types/Vehicle";

// 서버(VehiclePhotoAngle, VehiclePhotoPolicy)와 같은 규칙. 바꾸려면 양쪽을 함께 바꾼다.
export const REQUIRED_ANGLES: VehiclePhotoAngle[] = [
  "FRONT",
  "BACK",
  "LEFT",
  "RIGHT",
];
export const MAX_ETC_PHOTOS = 6;

export const ANGLE_LABEL: Record<VehiclePhotoAngle, string> = {
  FRONT: "앞",
  BACK: "뒤",
  LEFT: "좌",
  RIGHT: "우",
  ETC: "추가",
};

// 사진을 보여줄 때의 이름. "운행 시작"이라고 쓰면 실제 운행 시작 버튼으로 오해할 수 있어
// 사진이라는 것이 드러나는 "출발/도착"을 쓴다 (운행 내역의 출발/도착과 같은 말).
export const PHOTO_PHASE_LABEL: Record<VehiclePhotoPhase, string> = {
  START: "출발 사진",
  END: "도착 사진",
};
export const PHOTO_PHASE_SHORT: Record<VehiclePhotoPhase, string> = {
  START: "출발",
  END: "도착",
};

// SDK 자체 재시도. 기본값(4회, 4초부터 지수 증가)은 끊긴 망에서 1분 가까이 기다리게 해서 줄였다.
export const UPLOAD_RETRY_OPTIONS = {
  maxTries: 3,
  retryDelayInMs: 1000,
  maxRetryDelayInMs: 4000,
};

/**
 * 화면의 사진 한 장.
 * - 새로 고른 사진: file 과 previewUrl 이 있고, 업로드가 끝나면 uploaded 가 채워진다 (재시도 때 다시 올리지 않음)
 * - 이미 저장된 사진(관리자 수정 화면): file 없이 uploaded 만 있다
 */
export interface VehiclePhotoDraft {
  key: string;
  angle: VehiclePhotoAngle;
  file?: File;
  previewUrl?: string;
  uploaded?: VehiclePhotoUpload;
}

export function createDraft(angle: VehiclePhotoAngle, file: File): VehiclePhotoDraft {
  return {
    key: uuid(),
    angle,
    file,
    previewUrl: URL.createObjectURL(file),
  };
}

/** 서버에 이미 저장된 사진을 수정 화면용 draft 로 바꾼다. 업로드 대상이 아니다. */
export function draftFromPhoto(photo: VehiclePhotoInfo): VehiclePhotoDraft {
  return {
    key: `saved-${photo.id}`,
    angle: photo.angle,
    uploaded: {
      angle: photo.angle,
      name: photo.name,
      url: photo.url,
      takenAt: photo.takenAt ?? undefined,
      takenAtSource: photo.takenAtSource ?? undefined,
    },
  };
}

/** 미리보기 주소. 저장된 사진은 SAS 토큰을 붙여야 보인다. */
export function draftImageSrc(
  draft: VehiclePhotoDraft,
  appendSas: (url: string) => string
): string {
  if (draft.previewUrl) return draft.previewUrl;
  return draft.uploaded ? appendSas(draft.uploaded.url) : "";
}

export function revokeDraft(draft: VehiclePhotoDraft) {
  if (draft.previewUrl) URL.revokeObjectURL(draft.previewUrl);
}

export function revokeDrafts(drafts: VehiclePhotoDraft[]) {
  drafts.forEach(revokeDraft);
}

/**
 * 저장된 사진 중 수정 후 목록에서 빠지는 장수. 서버는 방향+URL 이 같은 사진만 남기므로
 * 삭제한 사진과 다른 사진으로 바꾼 칸이 모두 여기에 센다.
 */
export function removedSavedCount(
  saved: VehiclePhotoInfo[],
  drafts: VehiclePhotoDraft[]
): number {
  const kept = new Set(
    drafts.flatMap((draft) =>
      draft.uploaded ? [`${draft.angle}|${draft.uploaded.url}`] : []
    )
  );
  return saved.filter((photo) => !kept.has(`${photo.angle}|${photo.url}`)).length;
}

export function missingAngles(drafts: VehiclePhotoDraft[]): VehiclePhotoAngle[] {
  return REQUIRED_ANGLES.filter(
    (angle) => !drafts.some((draft) => draft.angle === angle)
  );
}

export function isPhotoSetComplete(drafts: VehiclePhotoDraft[]): boolean {
  return missingAngles(drafts).length === 0;
}

export function etcCount(drafts: VehiclePhotoDraft[]): number {
  return drafts.filter((draft) => draft.angle === "ETC").length;
}

interface UploadParams {
  drafts: VehiclePhotoDraft[];
  reservationId: number;
  phase: VehiclePhotoPhase;
  /** 만료됐으면 갱신한 SAS URL 을 돌려주는 함수 (useSasToken().refreshSasToken) */
  getSasUrl: () => Promise<string>;
  onProgress?: (done: number, total: number) => void;
  /**
   * 한 장 올라갈 때마다 uploaded 가 채워진 draft 를 넘겨준다.
   * 중간에 끊겨도 호출한 쪽이 올라간 사진을 기억해 두면, 다시 저장할 때 그 사진은 건너뛴다.
   */
  onUploaded?: (draft: VehiclePhotoDraft) => void;
}

/**
 * 사진을 하나씩 Blob 에 올리고, uploaded 가 채워진 새 draft 목록을 돌려준다.
 * 이미 올라간 사진은 건너뛰므로, 실패한 뒤 다시 눌러도 재업로드하지 않는다.
 * 한 장이라도 끝내 실패하면 예외를 던진다. 그 전에 올라간 사진은 onUploaded 로 이미 알렸다.
 */
export async function uploadVehiclePhotos({
  drafts,
  reservationId,
  phase,
  getSasUrl,
  onProgress,
  onUploaded,
}: UploadParams): Promise<VehiclePhotoDraft[]> {
  const sasUrl = await getSasUrl();
  // 저장할 URL 은 SAS 없이, 서버가 아는 컨테이너 URL 그대로 만든다.
  const containerUrl = sasUrl.split("?")[0].replace(/\/$/, "");
  const containerClient = new BlobServiceClient(sasUrl, undefined, {
    retryOptions: UPLOAD_RETRY_OPTIONS,
  }).getContainerClient("");
  const mode = process.env.REACT_APP_ACTIVE_MODE ?? "local";

  const result: VehiclePhotoDraft[] = [];
  let done = 0;
  for (const draft of drafts) {
    if (draft.uploaded) {
      result.push(draft);
    } else if (!draft.file) {
      throw new Error("업로드할 사진 파일이 없습니다.");
    } else {
      // 줄이면 사진 안의 촬영 정보가 지워지므로 먼저 읽는다
      const capture = await readCaptureTime(draft.file);
      // 흠집을 가까이 찍는 추가 사진은 세부가 중요해 더 높은 화질로 둔다
      const blob = await compressImage(
        draft.file,
        draft.angle === "ETC" ? DETAIL_QUALITY : OVERVIEW_QUALITY
      );
      const name = `${phase}_${draft.angle}_${uuid()}.jpg`;
      const blobName = `${mode}/vehicle/${reservationId}/${name}`;
      await containerClient.getBlockBlobClient(blobName).uploadData(blob, {
        blobHTTPHeaders: { blobContentType: blob.type || "image/jpeg" },
      });
      const uploadedDraft: VehiclePhotoDraft = {
        ...draft,
        uploaded: {
          angle: draft.angle,
          name,
          url: `${containerUrl}/${blobName}`,
          ...(capture ? { takenAt: capture.takenAt, takenAtSource: capture.source } : {}),
        },
      };
      result.push(uploadedDraft);
      onUploaded?.(uploadedDraft);
    }
    done += 1;
    onProgress?.(done, drafts.length);
  }
  return result;
}

/** 업로드가 중간에 끊겼을 때 안내. 무엇을 하면 되는지와 사진이 남아 있다는 것을 알려준다. */
export function uploadInterruptedMessage(uploadedCount: number): string {
  const kept =
    uploadedCount > 0
      ? `찍은 사진은 그대로 있고, 이미 올라간 ${uploadedCount}장은 다시 올리지 않습니다.`
      : "찍은 사진은 그대로 있습니다.";
  return `사진 업로드가 중간에 끊겼습니다.<br>신호가 잡히는 곳에서 저장을 다시 눌러주세요.<br>${kept}`;
}
